import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios'
import {reportCatchError} from '../utils/errorReporter'
import {getLiveAuthToken} from './authTokenAccessor'

/**
 * Base API class for MSP NodeJS service communication
 * Provides common HTTP methods and error handling
 *
 * Environment Variables:
 * - VITE_BACKEND_URL: Base URL for the MSP NodeJS service (e.g., 'https://nodeprod.jobsource-msp.com/api')
 * - Falls back to '/api' if not set (for development)
 */
export class BaseApi {
  protected client: AxiosInstance

  constructor(baseURL?: string) {
    this.client = axios.create({
      baseURL: baseURL || import.meta.env.VITE_BACKEND_URL || '/api',
      timeout: 30000, // 30 seconds
      headers: {
        'Content-Type': 'application/json',
      },
    })

    // Add request interceptor for authentication
    this.client.interceptors.request.use(
      (config) => {
        // Prefer the LIVE Redux token (same source as the RTK base query). BaseApi used to read only
        // the *persisted* copy in localStorage, which lags the live state by the redux-persist
        // debounce — so right after an IOP login these axios services sent requests with a stale/empty
        // token and got 401s until the first flush. Fall back to the persisted copy / legacy storage
        // for the brief window before the store registers its accessor or finishes rehydrating.
        let token: string | null = getLiveAuthToken()

        if (!token) {
          try {
            // Try to get from persisted Redux state
            const persistedState = localStorage.getItem('persist:root')
            if (persistedState) {
              const parsed = JSON.parse(persistedState)
              if (parsed.token) {
                // Remove quotes if the token is JSON stringified
                token = parsed.token.replace(/^"|"$/g, '')
              }
            }
          } catch (error) {
            reportCatchError('Error parsing persisted state:', error)
          }
        }

        // ALWAYS check fallback if no valid token found
        if (!token || token === 'null' || token === '') {
          token =
            localStorage.getItem('authToken') ||
            sessionStorage.getItem('authToken') ||
            localStorage.getItem('jwtToken') ||
            sessionStorage.getItem('jwtToken')
        }

        if (token) {
          config.headers.Authorization = `Bearer ${token}`
        }
        return config
      },
      (error) => {
        return Promise.reject(error)
      },
    )

    // Add response interceptor for common error handling
    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        return response
      },
      (error) => {
        // Log the error for debugging
        reportCatchError('API Error:', error)

        // Handle common error cases
        if (error.response?.status === 401) {
          // Check if we have a valid token - if we do, 401 means lack of permissions, not expired session
          const hasToken = this.hasValidToken()

          if (hasToken) {
            // User is authenticated but lacks permission for this resource
            // Don't logout, just reject the promise
            console.warn('Access denied - insufficient permissions for:', error.config?.url)
          } else {
            // No valid token - this is a true authentication failure
            // Unauthorized - clear auth state and redirect to login
            window.localStorage.removeItem('persist:root')
            window.localStorage.removeItem('persist:combined')
            window.location.href = '/signin'
          }
        } else if (error.response?.status === 403) {
          // Forbidden - user doesn't have permission
          console.warn('Access forbidden - insufficient permissions')
        } else if (error.response?.status >= 500) {
          // Server error
          reportCatchError('Server error occurred', error)
        }

        return Promise.reject(error)
      },
    )
  }

  /**
   * Check if a valid authentication token exists
   */
  private hasValidToken(): boolean {
    try {
      // Live token first (matches the request interceptor) so the lag window after an IOP login
      // isn't mistaken for "logged out".
      if (getLiveAuthToken()) {
        return true
      }
      // Check persisted Redux state
      const persistedState = localStorage.getItem('persist:root')
      if (persistedState) {
        const parsed = JSON.parse(persistedState)
        if (parsed.token) {
          const token = parsed.token.replace(/^"|"$/g, '')
          if (token && token !== 'null' && token !== '') {
            return true
          }
        }
      }

      // Check fallback locations
      const fallbackToken =
        localStorage.getItem('authToken') ||
        sessionStorage.getItem('authToken') ||
        localStorage.getItem('jwtToken') ||
        sessionStorage.getItem('jwtToken')

      return !!(fallbackToken && fallbackToken !== 'null' && fallbackToken !== '')
    } catch (error) {
      return false
    }
  }

  /**
   * Generic GET request
   */
  protected async get<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.get<T>(url, config)
  }

  /**
   * Generic POST request
   */
  protected async post<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.post<T>(url, data, config)
  }

  /**
   * Generic PUT request
   */
  protected async put<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.put<T>(url, data, config)
  }

  /**
   * Generic DELETE request
   */
  protected async delete<T>(url: string, config?: AxiosRequestConfig): Promise<AxiosResponse<T>> {
    return this.client.delete<T>(url, config)
  }

  /**
   * Generic PATCH request
   */
  protected async patch<T>(
    url: string,
    data?: unknown,
    config?: AxiosRequestConfig,
  ): Promise<AxiosResponse<T>> {
    return this.client.patch<T>(url, data, config)
  }

  /**
   * When a request uses responseType:'blob', Axios stores the error body as a Blob
   * instead of a parsed object.  extractErrorMessage then reads undefined from
   * response.data.message and falls back to the generic Axios status string.
   *
   * This helper reads the Blob, parses the JSON, and returns the server's
   * `message` string — or null if the response isn't a parseable Blob.
   *
   * Usage in a catch block:
   *   const message = await this.extractBlobErrorMessage(error)
   *   throw message ? new Error(message) : error
   */
  protected async extractBlobErrorMessage(error: unknown): Promise<string | null> {
    if (!error || typeof error !== 'object' || !('response' in error)) return null
    const blob = (error as {response?: {data?: Blob}}).response?.data
    if (!(blob instanceof Blob)) return null
    try {
      const text = await blob.text()
      const json = JSON.parse(text) as {message?: string}
      return json.message || null
    } catch {
      return null
    }
  }
}
